import { Appearance } from "@ui/Appearance";
import { Card } from "@ui/Card";
import { useSelectOnlyCombobox } from "@ui/hooks/useSelectOnlyCombobox";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Pill } from "@ui/Pill";
import { HStack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { createMemo, createResource, For, Show } from "solid-js";
import { Portal } from "solid-js/web";
import "@ui/Dropdown.css";
import {
	FALLBACK_QUESTION_EFFORTS,
	isQuestionEffortAllowed,
	loadQuestionEfforts,
	maxQuestionEffort,
	QUESTION_EFFORT_SYMBOLS,
	type QuestionEffort,
} from "./questionEfforts";
import "./QuestionBoxEffort.css";

/** How long the list stays open after a choice, pulsing the chosen effort. */
const CHOSEN_PULSE_MS = 300;

/** Chooses how much research goes into an answer. */
export const QuestionBoxEffort = (props: {
	value: QuestionEffort;
	onChange: (effort: QuestionEffort) => void;
	disabled?: boolean;
}) => {
	const [efforts] = createResource(() => loadQuestionEfforts(), {
		initialValue: FALLBACK_QUESTION_EFFORTS,
	});
	const selected = () =>
		efforts().find((effort) => effort.id === props.value) ?? efforts()[0];

	const selectedIndex = createMemo(() => {
		const index = efforts().findIndex((effort) => effort.id === props.value);
		return index >= 0 ? index : 0;
	});

	const combobox = useSelectOnlyCombobox({
		disabled: () => props.disabled,
		optionCount: () => efforts().length,
		selectedIndex,
		getOptionLabel: (index) => efforts()[index]?.display_name ?? "",
		isOptionDisabled: (index) => {
			const effort = efforts()[index];
			return effort !== undefined && !isQuestionEffortAllowed(effort.id);
		},
		onSelect: (index) => {
			const effort = efforts()[index];
			if (effort) {
				props.onChange(effort.id);
			}
		},
		placement: () => "bottom end",
		closeDelayMs: CHOSEN_PULSE_MS,
	});

	return (
		<div
			ref={combobox.setRootEl}
			class="gvtui-dropdown gvtui-dropdown--has-trigger"
			classList={{ "gvtui-dropdown--open": combobox.isOpen() }}
		>
			<div
				class="gvtui-dropdown__button--trigger gvtui-dropdown__button--trigger-none gvtui-question-box-effort__trigger"
				tabIndex={props.disabled ? -1 : 0}
				aria-disabled={props.disabled ? true : undefined}
				aria-label={`Effort: ${selected().display_name}`}
				ref={combobox.setTriggerEl}
				{...combobox.triggerProps()}
				onClick={combobox.onTriggerClick}
				onKeyDown={combobox.onTriggerKeyDown}
			>
				<Pill theme="ink" variant="outline" size="small" aria-hidden="true">
					<MaterialSymbol
						symbol={QUESTION_EFFORT_SYMBOLS[selected().id].symbol}
						color={
							props.disabled
								? "fg3"
								: QUESTION_EFFORT_SYMBOLS[selected().id].color
						}
					/>
					<Text as="span" size="small" color="fg3">
						Effort:
					</Text>
					<Text
						as="span"
						size="small"
						weight="bold"
						color={props.disabled ? "fg3" : "ink"}
					>
						{selected().display_name}
					</Text>
					<MaterialSymbol symbol="keyboard_arrow_down" color="fg3" />
				</Pill>
			</div>
			<Show when={combobox.isOpen()}>
				<Portal>
					<Appearance
						scheme="light"
						ref={combobox.setListboxEl}
						class="gvtui-dropdown__listbox gvtui-dropdown__listbox--panel gvtui-question-box-effort__listbox"
						data-placement={combobox.popupPosition()?.placement ?? "bottom end"}
						style={{
							"max-width": "calc(100vw - 2rem)",
							top: combobox.popupPosition()?.top ?? "0px",
							left: combobox.popupPosition()?.left ?? "0px",
							"min-width": combobox.popupPosition()?.minWidth ?? "100px",
							"--gvtui-question-box-effort-pulse-duration": `${CHOSEN_PULSE_MS}ms`,
						}}
						{...combobox.listboxProps()}
						aria-label="Effort"
					>
						<VStack spacing="medium">
							<Text
								family="mono"
								size="small"
								weight="bold"
								color="fg2"
								transform="uppercase"
							>
								Effort
							</Text>
							<For each={efforts()}>
								{(effort, index) => {
									const optionProps = () => combobox.getOptionProps(index());
									const isKeyboardFocused = () =>
										combobox.isOpen() &&
										index() === combobox.focusedIndex();
									const isCommitted = () => effort.id === props.value;
									const isDisabled = () => !isQuestionEffortAllowed(effort.id);
									return (
										<div
											class="gvtui-question-box-effort__option"
											classList={{
												"gvtui-question-box-effort__option--focused":
													isKeyboardFocused(),
												"gvtui-question-box-effort__option--selected":
													isCommitted(),
												"gvtui-question-box-effort__option--chosen":
													isCommitted() && combobox.isClosingAfterSelect(),
											}}
											{...optionProps()}
										>
											<Card as="div" borderRadius="standard" noBorder>
												<HStack align="center" spacing="large">
													<MaterialSymbol
														symbol={QUESTION_EFFORT_SYMBOLS[effort.id].symbol}
														color={
															isDisabled()
																? "fg5"
																: QUESTION_EFFORT_SYMBOLS[effort.id].color
														}
														relativeSize="plus"
													/>
													<VStack spacing="none" grow>
														<Text
															weight="bold"
															color={isDisabled() ? "fg5" : "ink"}
														>
															{effort.display_name}
														</Text>
														<Text
															size="small"
															color={isDisabled() ? "fg5" : "fg3"}
														>
															{effort.description}
														</Text>
													</VStack>
													{/* Hidden rather than removed, so the description doesn't rewrap when the selection changes. */}
													<MaterialSymbol
														symbol={isDisabled() ? "lock" : "check"}
														color={isDisabled() ? "fg5" : "primary"}
														style={{
															visibility:
																isDisabled() || isCommitted() ? "visible" : "hidden",
														}}
													/>
												</HStack>
											</Card>
										</div>
									);
								}}
							</For>
							<Show when={maxQuestionEffort() !== null}>
								<Text size="small" color="fg3">
									Sign in to access higher effort levels.
								</Text>
							</Show>
						</VStack>
					</Appearance>
				</Portal>
			</Show>
		</div>
	);
};
